import React, { useState } from "react";

// Two-digit zero-pad (used for ICS and Google calendar date strings).
function pad(n) { return String(n).padStart(2, "0"); }

// Formats a Date as YYYYMMDDTHHMM00 (ICS / Google Calendar basic format).
function toCalDate(d) {
  return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}T${pad(d.getHours())}${pad(d.getMinutes())}00`;
}

// Calendar booking modal — extracted from TradieDashboard for readability.
// Handles date/time selection, adds-to-calendar deep links, and dispatches
// `onConfirm(isoString)` when the tradie confirms the booking.
export default function BookJobModal({ lead, onConfirm, onClose }) {
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(today);
  const [time, setTime] = useState("09:00");
  const [saving, setSaving] = useState(false);

  const address = lead.form?.address || "";
  const title = `Job: ${lead.customer} - ${lead.trade}`;

  function getStart() {
    return new Date(`${date}T${time}:00`);
  }
  function getEnd() {
    const d = getStart();
    d.setHours(d.getHours() + 2);
    return d;
  }

  function googleUrl() {
    const s = toCalDate(getStart());
    const e = toCalDate(getEnd());
    const params = new URLSearchParams({
      action: "TEMPLATE",
      text: title,
      dates: `${s}/${e}`,
      details: "Remote Quote job booking.",
      location: address,
    });
    return `https://calendar.google.com/calendar/render?${params}`;
  }

  function outlookUrl() {
    const s = getStart().toISOString();
    const e = getEnd().toISOString();
    const params = new URLSearchParams({
      subject: title,
      startdt: s,
      enddt: e,
      body: "Remote Quote job booking.",
      location: address,
    });
    return `https://outlook.live.com/calendar/0/deeplink/compose?${params}`;
  }

  function downloadICS() {
    const s = toCalDate(getStart());
    const e = toCalDate(getEnd());
    const ics = [
      "BEGIN:VCALENDAR",
      "VERSION:2.0",
      "BEGIN:VEVENT",
      `DTSTART:${s}`,
      `DTEND:${e}`,
      `SUMMARY:${title}`,
      "DESCRIPTION:Remote Quote job booking.",
      `LOCATION:${address}`,
      "END:VEVENT",
      "END:VCALENDAR",
    ].join("\r\n");
    const blob = new Blob([ics], { type: "text/calendar" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "job-booking.ics";
    a.click();
    URL.revokeObjectURL(url);
  }

  async function handleConfirm() {
    setSaving(true);
    await onConfirm(getStart().toISOString());
    setSaving(false);
  }

  const CAL_BTN = {
    display: "inline-flex", alignItems: "center", gap: "6px",
    padding: "8px 14px", borderRadius: "8px", border: "none",
    fontSize: "0.85rem", fontWeight: 600, cursor: "pointer",
    textDecoration: "none",
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 10001,
        background: "rgba(0,0,0,0.65)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: "16px",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "var(--surface, #fff)",
          borderRadius: "16px", padding: "28px",
          width: "100%", maxWidth: "460px",
          boxShadow: "0 8px 40px rgba(0,0,0,0.3)",
        }}
      >
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "20px" }}>
          <div>
            <h3 style={{ margin: "0 0 4px" }}>Book job date</h3>
            <p className="muted small" style={{ margin: 0 }}>{lead.customer} · {lead.trade}</p>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", fontSize: "1.5rem", cursor: "pointer", lineHeight: 1, color: "var(--text-muted, #6b7280)" }}>×</button>
        </div>

        {/* Date + time pickers */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", marginBottom: "20px" }}>
          <div>
            <label className="muted small" style={{ display: "block", marginBottom: "4px" }}>Date</label>
            <input type="date" value={date} min={today} onChange={(e) => setDate(e.target.value)} style={{ width: "100%", padding: "8px 10px", borderRadius: "8px", border: "1px solid var(--border, #e5e7eb)", fontSize: "0.9rem" }} />
          </div>
          <div>
            <label className="muted small" style={{ display: "block", marginBottom: "4px" }}>Start time</label>
            <input type="time" value={time} onChange={(e) => setTime(e.target.value)} style={{ width: "100%", padding: "8px 10px", borderRadius: "8px", border: "1px solid var(--border, #e5e7eb)", fontSize: "0.9rem" }} />
          </div>
        </div>

        {/* Add to calendar */}
        <div style={{ background: "rgba(0,0,0,0.04)", borderRadius: "12px", padding: "14px", marginBottom: "20px" }}>
          <p className="muted small" style={{ margin: "0 0 10px", fontWeight: 600 }}>Add to calendar</p>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
            <a href={googleUrl()} target="_blank" rel="noopener noreferrer"
              style={{ ...CAL_BTN, background: "#fff", border: "1px solid #e5e7eb", color: "var(--dark)" }}>
              <svg width="15" height="15" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
              Google
            </a>
            <button onClick={downloadICS} style={{ ...CAL_BTN, background: "#fff", border: "1px solid #e5e7eb", color: "var(--dark)" }}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M17 2H7C5.9 2 5 2.9 5 4v16c0 1.1.9 2 2 2h10c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-5 18c-.83 0-1.5-.67-1.5-1.5S11.17 17 12 17s1.5.67 1.5 1.5S12.83 20 12 20zm5-4H7V4h10v12z"/></svg>
              Apple
            </button>
            <a href={outlookUrl()} target="_blank" rel="noopener noreferrer"
              style={{ ...CAL_BTN, background: "#fff", border: "1px solid #e5e7eb", color: "var(--dark)" }}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="#0078D4"><path d="M7 2H2v20h20v-5h-5v-2h5V9h-5V7h5V2H7zm10 0v3h3v2h-3v2h3v2h-3v2h3v3H9V4h8z"/></svg>
              Outlook
            </a>
          </div>
        </div>

        {/* Confirm */}
        <button
          onClick={handleConfirm}
          disabled={saving || !date || !time}
          style={{
            width: "100%", padding: "12px", borderRadius: "12px",
            border: "none", background: "var(--primary)", color: "#fff",
            fontWeight: 700, fontSize: "1rem", cursor: saving ? "not-allowed" : "pointer",
            opacity: saving ? 0.7 : 1,
          }}
        >
          {saving ? "Saving…" : "Confirm booking"}
        </button>
      </div>
    </div>
  );
}
